'use client'

import { motion, useInView } from 'framer-motion'
import { useRef, useState } from 'react'
import { RouteLink } from '@/lib/router'
import { capabilities } from './capabilities-data'

function CapabilityItem({
  capability,
  index,
}: {
  capability: (typeof capabilities)[number]
  index: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-50px' })
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <motion.div
      ref={ref}
      className="border-b border-border"
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
    >
      <button
        type="button"
        className="group flex w-full items-start justify-between py-10 text-left md:items-center md:py-14"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <div className="flex flex-1 flex-col gap-4 md:flex-row md:items-center md:gap-12">
          <span className="w-8 font-mono text-sm text-accent">{capability.id}</span>
          <h3 className="flex-1 font-serif text-2xl tracking-tight transition-colors duration-300 group-hover:text-accent md:text-4xl lg:text-5xl">
            {capability.title}
          </h3>
        </div>
        <motion.div
          className="ml-4 flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-border transition-all duration-300 group-hover:border-accent group-hover:bg-accent/10"
          animate={{ rotate: isExpanded ? 45 : 0 }}
          transition={{ duration: 0.3 }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M12 5v14M5 12h14" />
          </svg>
        </motion.div>
      </button>

      <motion.div
        className="overflow-hidden"
        initial={false}
        animate={{ height: isExpanded ? 'auto' : 0, opacity: isExpanded ? 1 : 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="pb-12 pl-0 md:pb-16 md:pl-20">
          <p className="mb-8 max-w-3xl font-serif text-lg italic leading-relaxed text-muted-foreground">
            {capability.description}
          </p>
          <ul className="mb-8 grid gap-4 md:grid-cols-2">
            {capability.services.map((service) => (
              <li key={service} className="flex items-center gap-3 text-sm">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                {service}
              </li>
            ))}
          </ul>
          {'href' in capability && capability.href && (
            <RouteLink
              to={capability.href}
              className="text-sm uppercase tracking-[0.15em] text-foreground underline-offset-4 hover:text-accent hover:underline"
            >
              Read more
            </RouteLink>
          )}
        </div>
      </motion.div>
    </motion.div>
  )
}

export function CapabilityList() {
  return (
    <div>
      {capabilities.map((capability, index) => (
        <CapabilityItem key={capability.id} capability={capability} index={index} />
      ))}
    </div>
  )
}
